/**
 * 文件描述：多业态装修模板页面字段配置
 * @author DIY
 * @createTime 2026-07-22
 */

/** 卡片列跨度：Element Plus 24 栅格下每行 4 张 */
export const CARD_COL_SPAN = 6

/** 卡片间距，单位 px */
export const CARD_GUTTER = 16

/** 骨架屏卡片数量 */
export const CARD_SKELETON_COUNT = 8

/** 模板状态选项 */
export const TEMPLATE_STATUS_OPTIONS = [
  { label: '草稿', value: 0, type: 'info' },
  { label: '已发布', value: 1, type: 'success' },
  { label: '已下线', value: 2, type: 'warning' }
] as const

/** 平台选项 */
export const PLATFORM_OPTIONS = [
  { label: 'H5', value: 'h5' },
  { label: '小程序', value: 'mp-weixin' },
  { label: 'App', value: 'app' }
] as const

/** 默认场景选项 */
export const DEFAULT_SCENE_OPTIONS = [
  { label: '首页', value: 'home' },
  { label: '详情页', value: 'detail' },
  { label: '列表页', value: 'list' },
  { label: '自定义页面', value: 'custom' }
] as const

/** Element Plus 标签类型 */
export type ElementTagType = 'primary' | 'success' | 'warning' | 'info' | 'danger'

/** 业态标签颜色映射 */
export const INDUSTRY_TAG_TYPE_MAP: Record<string, ElementTagType> = {
  mall: 'warning',
  hotel: 'primary',
  activity: 'success',
  service: 'success',
  course: 'primary',
  scenic: 'primary',
  ticket: 'danger',
  content: 'info',
  match: 'warning',
  survey: 'danger',
  live: 'danger',
  'qiling-ai': 'primary'
}

/** 业态默认图标映射 */
export const INDUSTRY_ICON_MAP: Record<string, string> = {
  mall: 'ep:goods',
  hotel: 'ep:office-building',
  activity: 'ep:calendar',
  service: 'ep:service',
  course: 'ep:reading',
  scenic: 'ep:place',
  ticket: 'ep:ticket',
  content: 'ep:document',
  match: 'ep:trophy',
  survey: 'ep:edit-pen',
  live: 'ep:video-camera',
  'qiling-ai': 'ep:magic-stick'
}

/** 不同业态支持的场景配置 */
export const INDUSTRY_SCENE_OPTIONS: Record<string, typeof DEFAULT_SCENE_OPTIONS> = {
  mall: DEFAULT_SCENE_OPTIONS,
  hotel: DEFAULT_SCENE_OPTIONS,
  activity: DEFAULT_SCENE_OPTIONS,
  service: DEFAULT_SCENE_OPTIONS,
  course: DEFAULT_SCENE_OPTIONS,
  scenic: DEFAULT_SCENE_OPTIONS,
  ticket: DEFAULT_SCENE_OPTIONS,
  content: DEFAULT_SCENE_OPTIONS,
  match: DEFAULT_SCENE_OPTIONS,
  survey: DEFAULT_SCENE_OPTIONS,
  live: DEFAULT_SCENE_OPTIONS,
  'qiling-ai': DEFAULT_SCENE_OPTIONS
}

/**
 * 获取状态名称
 *
 * @param status 状态值
 * @return 状态名称
 */
export const getStatusLabel = (status?: number) => {
  return TEMPLATE_STATUS_OPTIONS.find((item) => item.value === status)?.label || '草稿'
}

/**
 * 获取状态标签类型
 *
 * @param status 状态值
 * @return Element Plus 标签类型
 */
export const getStatusType = (status?: number) => {
  return TEMPLATE_STATUS_OPTIONS.find((item) => item.value === status)?.type || 'info'
}

/**
 * 获取场景名称
 *
 * @param scene 场景值
 * @return 场景名称
 */
export const getSceneLabel = (scene?: string) => {
  return DEFAULT_SCENE_OPTIONS.find((item) => item.value === scene)?.label || '首页'
}

/**
 * 获取业态标签类型
 *
 * @param industry 业态类型
 * @return Element Plus 标签类型
 */
export const getIndustryTagType = (industry?: string): ElementTagType => {
  return INDUSTRY_TAG_TYPE_MAP[industry || ''] || 'info'
}

/**
 * 获取业态图标
 *
 * @param industry 业态类型
 * @param icon 后端图标
 * @return 图标名称
 */
export const getIndustryIcon = (industry?: string, icon?: string) => {
  return icon || INDUSTRY_ICON_MAP[industry || ''] || 'ep:grid'
}
